import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { BreadCrumbs } from "components/ProductConfigurator/components/Breadcrumb/BreadCrumbs";
import type { BreadcrumbItem } from "components/ProductConfigurator/components/Breadcrumb/BreadCrumbs";
import { useConfiguratorPopupContext } from "providers";

import {
  useProductsOverviewActions,
  useProductsOverviewStore,
} from "stores/productsOverviewStore";
import { useProject } from "stores/project";
import { useAllProducts } from "hooks/productHooks/useAllProducts";
import { useTranslate } from "hooks";
import { useHasProductChanged } from "stores/product";
import type { ProductNode } from "interfaces/product/product.types";

type FlatProductNode = Omit<ProductNode, "nodes"> & {
  keyList?: string[];
};

const getFlat = (tree: ProductNode[]): FlatProductNode[] =>
  tree.flatMap(({ nodes, ...item }) =>
    nodes ? [item, ...getFlat(nodes)] : item,
  );

const generateBreadcrumbs = (
  keyList: string[],
  allItems: FlatProductNode[],
): (FlatProductNode | undefined)[] =>
  keyList.map((item) => allItems.find((product) => product?.key === item));

type ProductBreadcrumbProps = {
  productName?: string | null;
};

export const ProductBreadcrumb = Scrivito.connect(
  ({ productName = null }: ProductBreadcrumbProps) => {
    const [flattedProducts, setFlattedProducts] = useState<FlatProductNode[]>(
      [],
    );
    const translate = useTranslate();
    const { productId } = Scrivito.currentPageParams();
    const project = useProject();
    const hasProductChanged = useHasProductChanged();
    const configuratorPopupContext =
      useConfiguratorPopupContext() as NonNullable<
        ReturnType<typeof useConfiguratorPopupContext>
      >;
    const { showConfiguratorPopup } = configuratorPopupContext;

    const allProducts = useAllProducts();

    const selectedCategory = useProductsOverviewStore((state) => {
      const prodCategId = state.productCategoryId;
      if (!prodCategId) {
        return [];
      }
      return Array.isArray(prodCategId) ? prodCategId : [prodCategId];
    });

    const { setProductCategoryId, navigateToProductListPage } =
      useProductsOverviewActions();

    const handleLeaveProductConfigurator = useCallback(
      (navigate: () => void) => {
        if (productId && hasProductChanged) {
          showConfiguratorPopup(navigate);
          return;
        }

        navigate();
      },
      [productId, hasProductChanged, showConfiguratorPopup],
    );

    useEffect(() => {
      if (allProducts) {
        setFlattedProducts(getFlat(allProducts as ProductNode[]));
      }
    }, [allProducts]);

    useEffect(() => {
      if (flattedProducts?.length && selectedCategory?.length) {
        setProductCategoryId(selectedCategory);
      }
    }, [selectedCategory, flattedProducts, setProductCategoryId]);

    const breadcrumbsList = useMemo<BreadcrumbItem[] | undefined>(() => {
      const { parentId, seriesId } = Scrivito.currentPageParams();
      const seriesIdNum = Number(seriesId);
      const matchedProduct =
        flattedProducts.find(
          (product) =>
            product.name === productName &&
            (!parentId || parentId === product.parentId),
        ) ||
        flattedProducts.find(
          (product) =>
            Number.isFinite(seriesIdNum) && product.serieId === seriesIdNum,
        );

      const breadcrumbsKeyList =
        (selectedCategory.length && selectedCategory) ||
        matchedProduct?.keyList;

      const productListBreadcrumbItem = {
        title: translate("PRODUCTS_OVERVIEW_PRODUCTS_LIST_TITLE"),
        handleClick: () =>
          handleLeaveProductConfigurator(() => {
            navigateToProductListPage({ projectId: project?.id });
          }),
      };

      if (flattedProducts?.length && breadcrumbsKeyList?.length) {
        return [
          productListBreadcrumbItem,
          ...generateBreadcrumbs(breadcrumbsKeyList, flattedProducts)
            .filter((item): item is FlatProductNode => Boolean(item))
            .map((item) => ({
              ...item,
              title: item.name ?? "",
              handleClick: () => {
                setProductCategoryId(item.keyList ?? []);
                return item.serieId !== undefined
                  ? undefined
                  : handleLeaveProductConfigurator(() => {
                      navigateToProductListPage({
                        projectId: project?.id,
                        shouldKeepProductCategories: true,
                      });
                    });
              },
            })),
        ];
      }

      if (breadcrumbsKeyList) {
        return [productListBreadcrumbItem];
      }

      return undefined;
    }, [
      selectedCategory,
      flattedProducts,
      translate,
      productName,
      navigateToProductListPage,
      project?.id,
      setProductCategoryId,
      handleLeaveProductConfigurator,
    ]);

    return (
      breadcrumbsList && (
        <BreadCrumbs
          classNames={cx("mb-3 font-md")}
          breadcrumbList={breadcrumbsList}
        />
      )
    );
  },
);
